/**
=========================================================

* Tazi Rides Ltd has developed this dashboard based on a further configured version of the original template.
* Website: https://www.tazirides.co.uk
* All rights reserved.

=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

// @mui material components
import React, { useCallback, useMemo, useState } from 'react'

import { Dialog } from '@mui/material'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import { FirebaseError } from 'firebase/app'
import { updatePassword } from 'firebase/auth'

import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDInput from 'components/MDInput'
import MDTypography from 'components/MDTypography'
import { useAuth } from 'context/auth'

const PASSWORD_REQUIREMENTS = ['Min 6 characters', 'One number (2 are recommended)'] as const
const MIN_PASSWORD_LENGTH = 6

const validatePassword = (password: string, confirmPassword: string) => {
	const minLengthValid = password.length >= MIN_PASSWORD_LENGTH
	const containsNumber = /\d/.test(password)
	const passwordsMatch = password === confirmPassword

	return {
		isValid: minLengthValid && containsNumber && passwordsMatch,
		minLengthValid,
		containsNumber,
		passwordsMatch,
	}
}

function ChangePassword(): JSX.Element {
	const { user } = useAuth()
	const [password, setPassword] = useState('')
	const [confirmPassword, setConfirmPassword] = useState('')
	const [dialogOpen, setDialogOpen] = useState(false)
	const [dialogMsg, setDialogMsg] = useState<{ header: string; content: string }>()
	const [isLoading, setIsLoading] = useState(false)

	const handleChangePasswordClick = useCallback(async () => {
		const validation = validatePassword(password, confirmPassword)

		if (!validation.isValid) {
			setDialogMsg({
				header: 'Error Updating Password ❌',
				content:
					'The passwords must match, have a minimum of 6 characters and at least one number.',
			})
			setDialogOpen(true)
			return
		}

		setIsLoading(true)
		try {
			await updatePassword(user.auth, password)
			setDialogMsg({
				header: 'Password Updated Successfully ✅',
				content: 'Thank you, please continue as normal.',
			})
			setDialogOpen(true)
			setPassword('')
			setConfirmPassword('')
		} catch (error) {
			console.error('Error Updating Password ❌', error)
			const firebaseError = error as FirebaseError

			if (firebaseError.code === 'auth/requires-recent-login') {
				setDialogMsg({
					header: 'Please Re-Sign In to Update Password',
					content:
						'You need to be recently signed in to update your password, please sign out and back in again to update.',
				})
			} else {
				setDialogMsg({
					header: 'Error Updating Password',
					content: 'There was an error updating your password.',
				})
			}
			setDialogOpen(true)
		} finally {
			setIsLoading(false)
		}
	}, [password, confirmPassword, user.auth])

	const renderPasswordRequirements = useMemo(
		() =>
			PASSWORD_REQUIREMENTS.map((item, key) => (
				<MDBox
					key={`requirement-${key}`}
					component="li"
					color="text"
					fontSize="1.25rem"
					lineHeight={1}
				>
					<MDTypography variant="button" color="text" fontWeight="regular" verticalAlign="middle">
						{item}
					</MDTypography>
				</MDBox>
			)),
		[]
	)

	const handleDialogClose = useCallback(() => setDialogOpen(false), [])

	return (
		<Card id="change-password">
			<Dialog open={dialogOpen} onClose={handleDialogClose}>
				<MDBox width={500} height={150} style={{ backgroundColor: '#fff' }}>
					<MDBox
						p={3}
						display={'flex'}
						flexDirection={'column'}
						justifyContent={'center'}
						alignItems={'center'}
						width={'100%'}
						gap={1}
					>
						<MDTypography variant="h4">{dialogMsg?.header}</MDTypography>
						<MDTypography variant="body2" textAlign="center">
							{dialogMsg?.content}
						</MDTypography>
					</MDBox>
				</MDBox>
			</Dialog>
			<MDBox p={3}>
				<MDTypography variant="h5">Change Password</MDTypography>
			</MDBox>
			<MDBox component="form" pb={3} px={3}>
				<Grid container spacing={3}>
					<Grid item xs={12}>
						<MDInput
							fullWidth
							label="New Password"
							value={password}
							inputProps={{ type: 'password', autoComplete: '' }}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
						/>
					</Grid>
					<Grid item xs={12}>
						<MDInput
							fullWidth
							label="Confirm New Password"
							value={confirmPassword}
							inputProps={{ type: 'password', autoComplete: '' }}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
								setConfirmPassword(e.target.value)
							}
						/>
					</Grid>
				</Grid>
				<MDBox mt={6} mb={1}>
					<MDTypography variant="h5">Password requirements</MDTypography>
				</MDBox>
				<MDBox mb={1}>
					<MDTypography variant="body2" color="text">
						Please follow this guide for a strong password
					</MDTypography>
				</MDBox>
				<MDBox display="flex" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap">
					<MDBox component="ul" m={0} pl={3.25} mb={{ xs: 8, sm: 0 }}>
						{renderPasswordRequirements}
					</MDBox>
					<MDBox ml="auto">
						<MDButton
							variant="gradient"
							color="secondary"
							size="small"
							disabled={isLoading}
							onClick={handleChangePasswordClick}
						>
							{isLoading ? 'Updating...' : 'update password'}
						</MDButton>
					</MDBox>
				</MDBox>
			</MDBox>
		</Card>
	)
}

export default ChangePassword
